import * as React from 'react'
import { cn } from '@/lib/utils'
import { Icon } from '@/design-system/atoms/Icon'

export interface BreadcrumbItem {
  label: React.ReactNode
  href?: string
  onClick?: () => void
}

export interface BreadcrumbProps extends React.HTMLAttributes<HTMLElement> {
  items: BreadcrumbItem[]
  separator?: React.ReactNode
}

export const Breadcrumb = React.forwardRef<HTMLElement, BreadcrumbProps>(
  ({ items, separator, className, ...rest }, ref) => (
    <nav
      ref={ref}
      aria-label="Breadcrumb"
      className={cn('text-sm', className)}
      {...rest}
    >
      <ol className="flex flex-wrap items-center gap-1 text-gray-500 dark:text-gray-400">
        {items.map((item, i) => {
          const last = i === items.length - 1
          const content =
            item.href && !last ? (
              <a
                href={item.href}
                className="rounded-sm hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:hover:text-white"
              >
                {item.label}
              </a>
            ) : item.onClick && !last ? (
              <button
                type="button"
                onClick={item.onClick}
                className="rounded-sm hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:hover:text-white"
              >
                {item.label}
              </button>
            ) : (
              <span
                className={cn(
                  last && 'font-medium text-gray-900 dark:text-white'
                )}
                aria-current={last ? 'page' : undefined}
              >
                {item.label}
              </span>
            )
          return (
            <li key={i} className="flex items-center gap-1">
              {content}
              {!last && (
                <span
                  aria-hidden="true"
                  className="text-gray-400 dark:text-gray-600"
                >
                  {separator ?? <Icon name="chevron-right" size={14} />}
                </span>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
)

Breadcrumb.displayName = 'Breadcrumb'
